import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import Button from '../components/Button';

const Resources = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      <Helmet>
        <title>Moving Resources &amp; Guides – MyMoveAdvisor</title>
        <meta name="description" content="Helpful moving resources, checklists, and guides to make your move stress-free. Tips from professional movers across Canada." />
        <meta property="og:title" content="Moving Resources &amp; Guides – MyMoveAdvisor" />
        <meta property="og:description" content="Helpful moving resources, checklists, and guides to make your move stress-free." />
        <link rel="canonical" href="https://mymoveadvisor.ca/resources" />
      </Helmet>
      {/* Header Section */}}
      <div className="bg-gray-900 border-b border-white/10 pt-32 pb-20 overflow-hidden relative">
        <div className="absolute top-0 right-0 w-1/2 h-full bg-primary/5 blur-[120px] rounded-full -translate-y-1/2 translate-x-1/2"></div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-3xl">
            <nav className="flex mb-8 text-sm font-medium">
              <Link to="/" className="text-gray-400 hover:text-white transition-colors">Home</Link>
              <span className="text-gray-600 mx-3">/</span>
              <span className="text-primary">Mover Resources</span>
            </nav>
            <h1 className="text-4xl md:text-6xl font-black text-white mb-6 leading-tight">
              Mover Success <span className="text-primary text-outline-primary">Hub</span>
            </h1>
            <p className="text-xl text-gray-400 leading-relaxed">
              Expert guides, growth strategies, and step-by-step instructions to help your moving business thrive on MyMoveAdvisor.
            </p>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-16">
        <div className="grid lg:grid-cols-3 gap-12">
          {/* Main Content Area */}
          <div className="lg:col-span-2 space-y-16">
            {/* Main Guide Article */}
            <article className="bg-white rounded-[2.5rem] overflow-hidden border border-gray-100 shadow-[0_10px_40px_-15px_rgba(0,0,0,0.05)]">
              <div className="relative h-96">
                <img
                  src="https://images.pexels.com/photos/4246061/pexels-photo-4246061.jpeg?auto=compress&cs=tinysrgb&w=1200"
                  alt="Mover Registration Guide"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent"></div>
                <div className="absolute bottom-8 left-8 right-8">
                  <div className="flex gap-3 mb-4">
                    <span className="bg-primary text-white text-[10px] px-3 py-1 rounded-full font-bold uppercase tracking-wider">Step-by-Step Guide</span>
                    <span className="bg-white/10 backdrop-blur-md text-white text-[10px] px-3 py-1 rounded-full font-bold uppercase tracking-wider">10 Min Read</span>
                  </div>
                  <h2 className="text-3xl md:text-4xl font-black text-white leading-tight">
                    The Complete Guide to Registering as a Mover on MyMoveAdvisor
                  </h2>
                </div>
              </div>

              <div className="p-10 md:p-14">
                <div className="prose prose-lg prose-orange max-w-none text-gray-600 space-y-8">
                  <p className="text-xl font-medium text-gray-900 leading-relaxed">
                    Ready to scale your moving business? MyMoveAdvisor connects you with thousands of verified local leads searching for reliable relocation partners. Here is your definitive roadmap to joining our network.
                  </p>

                  <section className="space-y-6">
                    <h3 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
                      <span className="flex-shrink-0 w-10 h-10 rounded-xl bg-orange-50 text-primary flex items-center justify-center font-black">1</span>
                      Primary Business Registration
                    </h3>
                    <p>
                      Begin by navigating to our <Link to="/register" className="text-primary font-bold hover:underline">Mover Registration Page</Link>. You'll need to provide your basic company details, including your business name, operating DOT numbers (if applicable), and primary service areas.
                    </p>
                    <div className="bg-gray-50 rounded-2xl p-6 border border-gray-100 text-sm">
                      <strong className="text-gray-900 block mb-2">💡 Pro Tip:</strong>
                      Ensure your business name matches your official filings to speed up the verification process.
                    </div>
                  </section>

                  <section className="space-y-6 pt-6 border-t border-gray-50">
                    <h3 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
                      <span className="flex-shrink-0 w-10 h-10 rounded-xl bg-orange-50 text-primary flex items-center justify-center font-black">2</span>
                      Team & Driver Profiles
                    </h3>
                    <p>
                      A great moving company is built on its people. After initial registration, you'll reach the team management section. Here you can add your crew members, drivers, and team leads.
                    </p>
                    <ul className="list-disc pl-6 space-y-2">
                      <li>Upload professional photos of your crew.</li>
                      <li>Highlight specialized certifications (Piano moving, antique handling).</li>
                      <li>Assign roles to manage leads in specific regions.</li>
                    </ul>
                  </section>

                  <section className="space-y-6 pt-6 border-t border-gray-50">
                    <h3 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
                      <span className="flex-shrink-0 w-10 h-10 rounded-xl bg-orange-50 text-primary flex items-center justify-center font-black">3</span>
                      The Verification Window
                    </h3>
                    <p>
                      To maintain our high standards, our compliance team reviews every application. We typically check for insurance validity, license compliance, and past service history. This process usually takes 24-48 business hours.
                    </p>
                  </section>

                  <section className="space-y-6 pt-6 border-t border-gray-50">
                    <h3 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
                      <span className="flex-shrink-0 w-10 h-10 rounded-xl bg-orange-50 text-primary flex items-center justify-center font-black">4</span>
                      Activating Your Lead Dashboard
                    </h3>
                    <p>
                      Once approved, you'll gain access to your **Mover Dashboard**. From here, you can set your notification preferences, define your "Ideal Client" profile, and start bidding on active leads.
                    </p>
                  </section>

                  <div className="bg-primary/5 rounded-3xl p-10 flex flex-col md:flex-row items-center gap-8 border border-primary/10 mt-12">
                    <div className="flex-1 text-center md:text-left">
                      <h4 className="text-2xl font-bold text-gray-900 mb-2">Ready to grow?</h4>
                      <p className="text-gray-600">Start your journey today and get your first lead within 48 hours.</p>
                    </div>
                    <Link to="/register">
                      <Button className="px-10 py-4 shadow-xl shadow-primary/20">Apply to Join Network</Button>
                    </Link>
                  </div>
                </div>
              </div>
            </article>
          </div>

          {/* Sidebar */}
          <div className="space-y-8">
            <div className="bg-white rounded-[2rem] p-8 border border-gray-100 shadow-sm">
              <h4 className="text-lg font-bold text-gray-900 mb-6">Related Resources</h4>
              <div className="space-y-6">
                {[
                  { title: 'How to increase your bid win rate', category: 'Growth' },
                  { title: 'Understanding Insurance requirements', category: 'Compliance' },
                  { title: 'Tips for getting 5-star reviews', category: 'Reputation' }
                ].map((item, i) => (
                  <div key={i} className="group cursor-pointer">
                    <span className="text-[10px] font-bold text-primary uppercase tracking-widest mb-1 block">{item.category}</span>
                    <h5 className="font-bold text-gray-800 group-hover:text-primary transition-colors leading-snug">{item.title}</h5>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Resources;
